import { urqlClient } from '@shared/api';

import {
  AI_CIO_CONVERSATION_DELETE,
  AI_CIO_CONVERSATION_RENAME,
  AI_CIO_CONVERSATION_UPSERT,
  AI_CIO_CONVERSATIONS_CLEAR,
} from '../graphql/mutations';
import { AI_CIO_CONVERSATIONS } from '../graphql/queries';
import { useConversationsStore, setHistorySync } from '../store/conversations.store';

interface ConversationRow {
  id: string;
  sessionId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Connects the conversations store to the CRM backend's AI CIO history index
 * (`aiCioConversations`). Call once at app start (after auth). Registers the
 * store's sync hooks and pulls the existing index into the store.
 */
export function initAiCioHistorySync(): void {
  syncActive = true;
  setHistorySync({
    onSessionBound: async (sessionId, title) => {
      const result = await urqlClient
        .mutation(AI_CIO_CONVERSATION_UPSERT, { input: { sessionId, title } })
        .toPromise();
      return !result.error;
    },
    onTitleChanged: (sessionId, title) => {
      void urqlClient
        .mutation(AI_CIO_CONVERSATION_RENAME, { input: { sessionId, title } })
        .toPromise();
    },
    onConversationDeleted: (sessionId) => {
      void urqlClient.mutation(AI_CIO_CONVERSATION_DELETE, { sessionId }).toPromise();
    },
    onHistoryCleared: async () => {
      const result = await urqlClient.mutation(AI_CIO_CONVERSATIONS_CLEAR, {}).toPromise();
      const cleared =
        !result.error &&
        (result.data as { aiCioConversationsClear?: boolean } | null | undefined)
          ?.aiCioConversationsClear === true;
      if (cleared) generation += 1;
      return cleared;
    },
  });

  void hydrate();
}

let syncActive = false;

// Bumped by a clear: a fetch that began before it may list the rows it removed.
let generation = 0;

/**
 * Re-pulls the server-side history index into the store. The history list calls this
 * every time it opens so it never depends on what the client happened to hold in
 * memory/localStorage. No-op until `initAiCioHistorySync` has run (e.g. in tests).
 */
export function refreshAiCioHistory(): Promise<void> {
  return syncActive ? hydrate() : Promise.resolve();
}

async function hydrate(): Promise<void> {
  const started = generation;
  try {
    const result = await urqlClient
      .query(AI_CIO_CONVERSATIONS, {}, { requestPolicy: 'cache-and-network' })
      .toPromise();
    const rows = (result.data as { aiCioConversations?: ConversationRow[] } | null)
      ?.aiCioConversations;
    // Only a successful fetch is the record of what is saved, an empty one included:
    // the store drops what it no longer lists.
    if (result.error || !Array.isArray(rows) || started !== generation) return;
    useConversationsStore.getState().hydrateFromHistory(
      rows.map((r) => ({
        sessionId: r.sessionId,
        title: r.title,
        createdAt: new Date(r.createdAt).getTime(),
        updatedAt: new Date(r.updatedAt).getTime(),
      })),
    );
  } catch {
    // History is a convenience layer — a fetch failure shouldn't break chat.
  }
}
